import { CounterParty } from '../model/counter-party.model'
import { Currency } from '../model/currency.model'
import { ExternalUnmatchedTransaction } from '../model/single-transaction/external-unmatched-transaction'
import {
  FXMappingModel,
  FXSpot,
} from '../model/single-transaction/fx-spot-trade/fx-spot.model'
import { getCounterPartyFromId, getCounterPartyFromName } from './common-utils'
import { TransactionConstants } from './constants'
import {
  getCurrencyFromCashSecurityId,
  getDefaultCurrency,
} from './currency-utils'

/**
 * Function for Create FxSpot mapping
 */
export function generateFxSpotCreateMapping(
  reconDate: string,
): FXMappingModel[] {
  const currency = getDefaultCurrency().code
  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account',
      value: '',
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: reconDate ?? new Date(),
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: reconDate ?? new Date(),
      errorMessage: '',
    },
    {
      id: 'tranType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Spot/Forward',
      value: TransactionConstants.FX_SPOT_OPTIONS[0].label,
      errorMessage: '',
    },
    {
      id: 'fromSecurityId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'From Currency',
      value: currency,
      errorMessage: '',
    },
    {
      id: 'toSecurityId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'To Currency',
      value: currency,
      errorMessage: '',
    },
    {
      id: 'fromUnits',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: `Sell : ${currency}`,
      value: '',
      errorMessage: '',
    },
    {
      id: 'sellToBuyUnitRatio',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'From/To',
      value: '',
      errorMessage: '',
    },
    {
      id: 'toUnits',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: `Buy : ${currency}`,
      value: '',
      errorMessage: '',
    },
    {
      id: 'buyToSellUnitRatio',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'To/From',
      value: '',
      errorMessage: '',
    },
    {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Counter Party',
      value: 'Unknown',
      errorMessage: '',
    },
  ]
}

/**
 * Function for Edit FxSpot mapping
 */
export function generateFxSpotEditMapping(
  fxTransaction: FXSpot,
  currencyList: Currency[],
): FXMappingModel[] {
  const toCurrency =
    getCurrencyFromCashSecurityId(fxTransaction.toSecurityId, currencyList)
      ?.code ?? ''
  const fromCurrency =
    getCurrencyFromCashSecurityId(fxTransaction.fromSecurityId, currencyList)
      ?.code ?? ''

  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Account',
      value: fxTransaction.accountId.toString(),
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: fxTransaction.entryDate,
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: fxTransaction.settleDate,
      errorMessage: '',
    },
    {
      id: 'tranType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Spot/Forward',
      value:
        TransactionConstants.FX_SPOT_OPTIONS.find(
          op => op.tranType === fxTransaction.tranType,
        )?.label ?? TransactionConstants.FX_SPOT_OPTIONS[0].label,
      errorMessage: '',
    },
    {
      id: 'fromSecurityId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'From Currency',
      value: fromCurrency,
      errorMessage: '',
    },
    {
      id: 'toSecurityId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'To Currency',
      value: toCurrency,
      errorMessage: '',
    },
    {
      id: 'fromUnits',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Sell : ' + fromCurrency,
      value: fxTransaction.fromUnits
        ? Math.abs(fxTransaction.fromUnits).toString()
        : '',
      errorMessage: '',
    },
    {
      id: 'sellToBuyUnitRatio',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'From/To',
      value:
        fxTransaction.fromUnits && fxTransaction.toUnits
          ? (
              Math.abs(fxTransaction.fromUnits) / fxTransaction.toUnits
            ).toString()
          : '',
      errorMessage: '',
    },
    {
      id: 'toUnits',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Buy : ' + toCurrency,
      value: fxTransaction.toUnits ? fxTransaction.toUnits.toString() : '',
      errorMessage: '',
    },
    {
      id: 'buyToSellUnitRatio',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'To/From',
      value:
        fxTransaction.fromUnits && fxTransaction.toUnits
          ? (
              fxTransaction.toUnits / Math.abs(fxTransaction.fromUnits)
            ).toString()
          : '',
      errorMessage: '',
    },
    {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Counter Party',
      value: fxTransaction.counterParty?.name ?? 'Unknown',
      errorMessage: '',
    },
  ]
}

/**
 * Function for External FxSpot mapping
 */
export function generateFxSpotExternalMapping(
  fxTransaction: ExternalUnmatchedTransaction,
  currencyList: Currency[],
  isAggregate: boolean,
): FXMappingModel[] {
  const toCurrency =
    getCurrencyFromCashSecurityId(fxTransaction?.securityId, currencyList)
      ?.code ?? ''
  const fromCurrency =
    getCurrencyFromCashSecurityId(fxTransaction?.fromSecurityId, currencyList)
      ?.code ?? ''

  return [
    {
      id: 'account',
      validityStatus: 'valid',
      type: 'string',
      isEditable: isAggregate,
      field: 'Account',
      value: fxTransaction.accountId.toString(),
      errorMessage: '',
    },
    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: fxTransaction.entryDate,
      errorMessage: '',
    },
    {
      id: 'settleDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Settle Date',
      value: fxTransaction.settleDate,
      errorMessage: '',
    },
    {
      id: 'tranType',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Spot/Forward',
      value:
        TransactionConstants.FX_SPOT_OPTIONS.find(
          op => op.tranType === fxTransaction.tranType,
        )?.label ?? TransactionConstants.FX_SPOT_OPTIONS[0].label,
      errorMessage: '',
    },
    {
      id: 'fromSecurityId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'From Currency',
      value: fromCurrency,
      errorMessage: '',
    },
    {
      id: 'toSecurityId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'To Currency',
      value: toCurrency,
      errorMessage: '',
    },
    {
      id: 'fromUnits',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Sell : ' + fromCurrency,
      value: fxTransaction.fromUnits
        ? Math.abs(fxTransaction.fromUnits).toString()
        : '',
      errorMessage: '',
    },
    {
      id: 'sellToBuyUnitRatio',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'From/To',
      value:
        fxTransaction.fromUnits && fxTransaction.units
          ? (Math.abs(fxTransaction.fromUnits) / fxTransaction.units).toString()
          : '',
      errorMessage: '',
    },
    {
      id: 'toUnits',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Buy : ' + toCurrency,
      value: fxTransaction.units ? fxTransaction.units.toString() : '',
      errorMessage: '',
    },
    {
      id: 'buyToSellUnitRatio',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'To/From',
      value:
        fxTransaction.fromUnits && fxTransaction.units
          ? (fxTransaction.units / Math.abs(fxTransaction.fromUnits)).toString()
          : '',
      errorMessage: '',
    },
    {
      id: 'counterParty',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Counter Party',
      value: fxTransaction.clearwaterCounterParty?.name ?? 'Unknown',
      errorMessage: '',
    },
  ]
}
